import {
  useTranslation,
  isPaymentFieldRequiredInCheckout,
  shouldCollectPaymentFieldInCheckout,
} from '@checkout/core'
import { useMemo, type ReactNode } from 'react'

import styles from './BillingInformationForm.module.css'
import { createCountryOptions, normalizeCountryCode } from './countries'
import {
  BILLING_INFORMATION_FIELD_REGISTRY,
  type BillingInformationFieldDefinition,
  type BillingInformationValue,
} from './fieldRegistry'
import { Input, SearchableSelect } from '../../components'

import type { SearchableSelectOption } from '../../components'
import type {
  BillingAddressInput,
  CheckoutPayerInput,
  CountryItem,
  CountrySubdivisionItem,
  PaymentMethodCheckoutFields,
} from '@checkout/core'

export interface BillingInformationFormProps {
  value: BillingInformationValue
  onChange: (value: BillingInformationValue) => void
  checkoutFields?: PaymentMethodCheckoutFields | null
  countryOptions: CountryItem[]
  regionOptions?: CountrySubdivisionItem[]
  disabled?: boolean
  disabledAppearance?: 'default' | 'muted'
  className?: string
}

export function createBillingInformationValue(country?: string): BillingInformationValue {
  return {
    line1: '',
    line2: '',
    city: '',
    country: normalizeCountryCode(country),
    postalCode: '',
    region: '',
    cardholderName: '',
    givenName: '',
    familyName: '',
    phoneNumber: '',
  }
}

function trimField(value: string): string {
  return value.trim()
}

function requiredPlaceholder(label: string, required: boolean): string {
  return required ? `*${label}` : label
}

const cardholderNamePattern = /^(?=.*\p{Script=Latin})[\p{Script=Latin}\p{Mark} .'-]+$/u

export function isSupportedCardholderName(value: string): boolean {
  const normalized = trimField(value)
  return normalized === '' || cardholderNamePattern.test(normalized)
}

function isSupportedCustomerName(value: string): boolean {
  const name = trimField(value)
  return new TextEncoder().encode(name).length <= 100 && !/\p{Cc}/u.test(name)
}

export function createRegionOptions(regions: CountrySubdivisionItem[]): SearchableSelectOption[] {
  return regions.map((region) => ({
    value: region.code,
    label: region.name,
    keywords: [region.code, region.iso_code, region.name].filter(Boolean),
  }))
}

export function getBillingFullName(value: BillingInformationValue): string | undefined {
  return trimField(value.cardholderName) || undefined
}

function shouldCollectField(
  definition: BillingInformationFieldDefinition,
  checkoutFields?: PaymentMethodCheckoutFields | null
): boolean {
  return shouldCollectPaymentFieldInCheckout({ fields: checkoutFields, field: definition.field })
}

function fieldIsRequired(
  definition: BillingInformationFieldDefinition,
  value: BillingInformationValue,
  checkoutFields?: PaymentMethodCheckoutFields | null
): boolean {
  return isPaymentFieldRequiredInCheckout({
    fields: checkoutFields,
    field: definition.field,
    country: normalizeCountryCode(value.country),
  })
}

export function hasCheckoutBillingInformationFields(
  checkoutFields?: PaymentMethodCheckoutFields | null
): boolean {
  return BILLING_INFORMATION_FIELD_REGISTRY.some((definition) =>
    shouldCollectField(definition, checkoutFields)
  )
}

export function isBillingInformationComplete(
  value: BillingInformationValue,
  checkoutFields?: PaymentMethodCheckoutFields | null
): boolean {
  for (const definition of BILLING_INFORMATION_FIELD_REGISTRY) {
    if (!shouldCollectField(definition, checkoutFields)) {
      continue
    }
    const fieldValue =
      definition.field === 'billing_country'
        ? normalizeCountryCode(value.country)
        : trimField(value[definition.valueKey])
    if (fieldIsRequired(definition, value, checkoutFields) && !fieldValue) {
      return false
    }
    if (
      (definition.field === 'given_name' || definition.field === 'family_name') &&
      !isSupportedCustomerName(fieldValue)
    ) {
      return false
    }
    if (
      definition.field === 'cardholder_name' &&
      fieldValue &&
      !isSupportedCardholderName(fieldValue)
    ) {
      return false
    }
  }
  return true
}

export function toBillingAddressInput(
  value: BillingInformationValue,
  checkoutFields?: PaymentMethodCheckoutFields | null
): BillingAddressInput | undefined {
  const address: BillingAddressInput = {}
  for (const definition of BILLING_INFORMATION_FIELD_REGISTRY) {
    if (!shouldCollectField(definition, checkoutFields)) {
      continue
    }
    const normalized =
      definition.field === 'billing_country'
        ? normalizeCountryCode(value.country)
        : trimField(value[definition.valueKey])
    if (!normalized) {
      continue
    }
    switch (definition.payloadKey) {
      case 'line1':
      case 'line2':
      case 'city':
      case 'country':
        address[definition.payloadKey] = normalized
        break
      case 'state':
        address.state = normalized
        break
      case 'postal_code':
        address.postalCode = normalized
        break
    }
  }
  return Object.keys(address).length > 0 ? address : undefined
}

export function toCheckoutBillingAddress(
  value: BillingInformationValue,
  checkoutFields?: PaymentMethodCheckoutFields | null
): Record<string, string> | undefined {
  const address: Record<string, string> = {}
  for (const definition of BILLING_INFORMATION_FIELD_REGISTRY) {
    if (!shouldCollectField(definition, checkoutFields)) {
      continue
    }
    const normalized =
      definition.field === 'billing_country'
        ? normalizeCountryCode(value.country)
        : trimField(value[definition.valueKey])
    if (normalized) {
      address[definition.payloadKey] = normalized
    }
  }
  return Object.keys(address).length > 0 ? address : undefined
}

export function toCheckoutPayerInput(
  value: BillingInformationValue,
  email: string,
  checkoutFields?: PaymentMethodCheckoutFields | null
): CheckoutPayerInput {
  const collectsCardholderName = shouldCollectPaymentFieldInCheckout({
    fields: checkoutFields,
    field: 'cardholder_name',
  })
  const collectsPhoneNumber = shouldCollectPaymentFieldInCheckout({
    fields: checkoutFields,
    field: 'phone_number',
  })
  return {
    email: trimField(email),
    name: collectsCardholderName ? getBillingFullName(value) : undefined,
    phone: collectsPhoneNumber ? trimField(value.phoneNumber) || undefined : undefined,
    billingAddress: toBillingAddressInput(value, checkoutFields),
  }
}

export function BillingInformationForm({
  value,
  onChange,
  checkoutFields,
  countryOptions: countries,
  regionOptions: regions = [],
  disabled = false,
  disabledAppearance = 'default',
  className,
}: BillingInformationFormProps) {
  const { t, i18n } = useTranslation()
  const language = i18n.language || 'en'
  const countryOptions = useMemo(
    () => createCountryOptions(countries, language),
    [countries, language]
  )
  const regionOptions = useMemo(() => createRegionOptions(regions), [regions])
  const cardholderNameError =
    shouldCollectPaymentFieldInCheckout({ fields: checkoutFields, field: 'cardholder_name' }) &&
    value.cardholderName &&
    !isSupportedCardholderName(value.cardholderName)
      ? t('validation.cardholderNameLatinOnly')
      : undefined

  const updateField = (field: keyof BillingInformationValue, nextValue: string) => {
    onChange({
      ...value,
      [field]: nextValue,
    })
  }

  const handleCountryChange = (country: string) => {
    const normalizedCountry = normalizeCountryCode(country)
    const shouldKeepRegion = normalizedCountry === normalizeCountryCode(value.country)
    onChange({
      ...value,
      country: normalizedCountry,
      region: shouldKeepRegion ? value.region : '',
    })
  }

  const renderField = (definition: BillingInformationFieldDefinition): ReactNode => {
    if (!shouldCollectField(definition, checkoutFields)) {
      return null
    }
    const required = fieldIsRequired(definition, value, checkoutFields)
    const label = t(definition.labelKey)
    const isNameField = definition.field === 'given_name' || definition.field === 'family_name'
    const nameError =
      isNameField && !isSupportedCustomerName(value[definition.valueKey])
        ? t('validation.nameInvalid')
        : undefined
    if (definition.kind === 'country') {
      return (
        <SearchableSelect
          key={definition.field}
          value={value.country}
          options={countryOptions}
          onChange={handleCountryChange}
          placeholder={requiredPlaceholder(label, required)}
          searchPlaceholder={t('billing.countrySearchPlaceholder')}
          emptyText={t('billing.noCountryResults')}
          disabled={disabled}
          disabledAppearance={disabledAppearance}
          aria-label={label}
        />
      )
    }
    if (definition.kind === 'region' && regionOptions.length > 0) {
      return (
        <SearchableSelect
          key={definition.field}
          value={value.region}
          options={regionOptions}
          onChange={(region) => updateField('region', region)}
          placeholder={requiredPlaceholder(label, required)}
          searchPlaceholder={label}
          emptyText={t('billing.noRegionResults')}
          disabled={disabled}
          disabledAppearance={disabledAppearance}
          aria-label={label}
        />
      )
    }
    return (
      <Input
        key={definition.field}
        value={value[definition.valueKey]}
        onChange={(event) => updateField(definition.valueKey, event.target.value)}
        placeholder={requiredPlaceholder(label, required)}
        disabled={disabled}
        disabledAppearance={disabledAppearance}
        autoComplete={definition.autoComplete}
        aria-label={label}
        maxLength={isNameField ? 100 : undefined}
        error={
          isNameField
            ? nameError
            : definition.field === 'cardholder_name'
              ? cardholderNameError
              : undefined
        }
        isFullWidth
      />
    )
  }

  return (
    <div className={`${styles.form} ${className ?? ''}`}>
      <h4 className={styles.title}>{t('checkout.section.billing')}</h4>
      <div className={styles.grid}>{BILLING_INFORMATION_FIELD_REGISTRY.map(renderField)}</div>
    </div>
  )
}

BillingInformationForm.displayName = 'BillingInformationForm'

export type { BillingInformationValue } from './fieldRegistry'
